import { HttpClient } from '@angular/common/http';
import { effect, Injectable, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { catchError, EMPTY, map, Observable, of } from 'rxjs';
import { User } from 'src/app/models/user.model';
import { AdminService } from 'src/app/services/admin.service';
import { environment } from '../../../environments/environment';
import { ALLOWED_TENANT_IDS, API_ENDPOINT, USE_BACKEND_AVATAR_API } from '../constants/app.constants';
import { UserPermission, userPermissionFromGroupId } from '../constants/permission.constants.enum';
import { LoadingService } from './loading.service';
import { MicrosoftGraphService, ProfileType } from './microsoft-graph.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  displayName = signal('');
  name = signal('');
  id = signal('');
  username = signal('');
  jobTitle = signal('');
  userPrincipalName = signal('');
  isEdit = signal(false);
  isRescreen = signal(false);
  isActive = signal(false);
  photo: WritableSignal<string | undefined> = signal(undefined);
  authenticated = signal<boolean>(false);
  permissions = signal<UserPermission[]>([]);
  hasNoPermissions = signal<boolean>(false);
  permissionsLoaded = signal<boolean>(false);
  permission$:Observable<UserPermission[]>;
  hasNoPermissions$: Observable<boolean>;
  tenantName: WritableSignal<string | undefined> = signal(undefined);

  avatarUri = environment.apiConfig.apiEndpointBase + API_ENDPOINT.AVATAR + '?userId=';

  constructor(
    private authService: MsalService,
    private graphService: MicrosoftGraphService,
    private loader: LoadingService,
    private adminService: AdminService,
    private http: HttpClient,
    private router: Router
  ) {
    effect(() => {
      const permissions = this.permissions();
      if (this.permissionsLoaded()) {
        const permissions = this.permissions();
        console.log(`user permission Ids: ${permissions}`);
        const isAdmin = permissions.includes(UserPermission.MedNecScreen_ISAdmin) ||
                permissions.includes(UserPermission.MedNecScreen_Quorum_ISAdmin);
        console.log(`user is ISADMIN: ${isAdmin}`);
        // Check if any of the fetched permissions are valid with UserPermission enum values
        const hasAnyValidPermission = permissions.some(p => Object.values(UserPermission).includes(p));

        if (hasAnyValidPermission) {
          this.hasNoPermissions.set(false);
        } else {
          this.hasNoPermissions.set(true);
          this.authenticated.set(false);
          this.router.navigate(['/not-authorized']);
        }
      } else {
        console.log('Permissions are still being loading...');
      }
      this.loader.loading.set(false);
    });
    this.permission$ = toObservable(this.permissions);
    this.hasNoPermissions$ = toObservable(this.hasNoPermissions);
   }

  setUserInfo(account: any) {
    account?.name && this.name.set(account.name);
    account?.username && this.username.set(account.username);
    this.authenticated.set(!!account);
    this.tenantName.set(ALLOWED_TENANT_IDS.find(t => t.id === account.tenantId)?.name);

    this.graphService.profile.subscribe((profile: ProfileType) => {
      if (profile) {
        profile.id && this.id.set(profile.id);
        profile.displayName && this.displayName.set(profile.displayName)
        profile.jobTitle && this.jobTitle.set(profile.jobTitle);
        profile.userPrincipalName && this.userPrincipalName.set(profile.userPrincipalName);
      }
    });

    this.adminService.getUserProfile(account.username).subscribe((response: { data: User }) => {
      if (response.data) {
        this.isEdit.set(response.data.isEdit);
        this.isRescreen.set(response.data.isRescreen);
        this.isActive.set(response.data.isActive);
        this.authenticated.set(this.isActive());

        if (!this.isActive()) {
          this.router.navigate(['/not-authorized']);
        }
      }
    });

    if (USE_BACKEND_AVATAR_API) {
      this.http.get(this.avatarUri + this.username()).pipe(
        map((r: any) => r.data),
        catchError(err => {
          console.warn('Backend avatar fetch failed:', err.message);
          return of(null);
        })
      ).subscribe(data => {
        if (data?.imageData) {
          this.photo.set(`data:image/png;base64,${data.imageData}`);
        } else if (data?.message) {
          console.log('avatar:', data.message);
        }
      });
    } else {
      this.graphService.getAvatar().pipe(
        map((res: ArrayBuffer) => new Blob([res], { type: 'image/jpeg' })),
        catchError(err => {
          if (err.status !== 404) {
            console.warn('Graph avatar fetch failed:', err.message);
          }
          return EMPTY;
        })
      ).subscribe(blob => {
        this.photo.set(URL.createObjectURL(blob));
      });
    }
    this.graphService.groups
      .pipe(
        // transform groupIds to UserPermission constants
        map(groupIds => {
          const mappedPermissions = groupIds.map(userPermissionFromGroupId).filter(p => !!p);
          return mappedPermissions;
        }),
        // uncomment for testing permissions
        // map(perms => [UserPermission.MedNecScreen_User]),
        // map(perms => [UserPermission.MedNecScreen_Admin]),
        // map(perms => [UserPermission.MedNecScreen_ISAdmin]),
        // map(perms => [UserPermission.MedNecScreen_Quorum_User]),
        // map(perms => [UserPermission.MedNecScreen_Quorum_Admin]),
        // map(perms => [UserPermission.MedNecScreen_Quorum_ISAdmin]),
        catchError(error => {
          console.error('Error fetching groups:', error);
          this.permissions.set([]); // Set permissions to empty in case of error
          return [];
        })
      )
      .subscribe(p => {
        console.log('Final permissions:', p);
        this.permissions.set(p);
        this.permissionsLoaded.set(true);
      });
  }

  logout(popup?: boolean) {
    if (popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: '/logged-out',
      });
    } else {
      this.authService.logoutRedirect();
    }
  }
}
